import { Alert, Box, Header, SpaceBetween, Table, Modal, Button, Checkbox } from '@amzn/awsui-components-console';
import React, { useState, useCallback } from 'react';
import { CustomStatusIndicator } from '../helpers';
import { ActivityInfoWithStatus } from '../activity/LastActivityExecution';
import { ActivityActions, StatusIdToCode } from 'src/constants/hammerstoneConstants';
import PipelineBulkActionProgressModal from './PipelineBulkActionProgressModal';
import store, { actions, useGroupName, useAlias } from 'src/data/redux';
import { fetchPipelineActivitiesAndInstances } from 'src/data/api/fetchFromAPI';
import { PutActivityInfo, DeleteActivityInfo } from 'src/lib/hammerstoneApi';
import keys from 'src/constants/hammerstoneConstantKeys';
import { useConditionalEffect } from 'src/commons';

export enum OperationStatus {
	PENDING = 'pending',
	IN_PROGRESS = 'in-progress',
	SUCCESS = 'success',
	ERROR = 'error',
}

// action to valid status mapping, note that a.status contains values like "SCHEDULED" while StatusCode literal is
// "Scheduled" so we will have to use StatusIdToCode
const actionStatusRules: Record<string, string[]> = {
	[ActivityActions.pause]: [StatusIdToCode[11]], // SCHEDULED
	[ActivityActions.resume]: [StatusIdToCode[14]], // PAUSED
	[ActivityActions.activate]: [StatusIdToCode[8], StatusIdToCode[7], StatusIdToCode[21]], // EDIT_PENDING, NEW, COMPLETED
	[ActivityActions.delete]: [], // Delete can be performed on any status
};

const ActionRulesExplanation = () => (
	<ul>
		{Object.entries(actionStatusRules)
			.filter(([, statuses]) => statuses.length > 0)
			.map(([action, statuses]) => (
				<li key={action}>
					Only <b>{statuses.join(', ')}</b> activities can be <b>{action.toUpperCase() + 'D'}</b>
				</li>
			))}
	</ul>
);

// Alert component for when some selected activities are invalid
const BulkActionInvalidStatusAlert = () => (
	<Alert
		data-testid="bulkActionInvalidStatusAlert"
		type="warning"
		header={<Header variant="h3">Multiple status types selected</Header>}
	>
		<Box>
			You have selected some activities with invalid statuses for the operation you are performing. Not all
			activities selected will be updated.
			<ActionRulesExplanation />
		</Box>
	</Alert>
);

// Alert component for when all selected activities are invalid
const BulkActionNoValidStatusAlert = ({ action }: { action: string }) => (
	<Alert
		data-testid="bulkActionNoValidStatusAlert"
		type="error"
		header={<Header variant="h3">Cannot perform {action.toLowerCase()} on selected status</Header>}
	>
		<Box>
			The action <b>{action.toLowerCase()}</b> cannot be performed on any of the selected activities. Please
			cancel and select the appropriate status types or actions.
			<ActionRulesExplanation />
		</Box>
	</Alert>
);

// Activity table column definitions used in both valid and invalid activity tables
const activityTableColumns = [
	{
		id: 'activityId',
		header: 'Activity ID',
		cell: ({ activityId }: ActivityInfoWithStatus) => activityId,
	},
	{
		id: 'status',
		header: 'Status',
		cell: ({ status }: ActivityInfoWithStatus) => <CustomStatusIndicator>{status}</CustomStatusIndicator>,
	},
];

export default function PipelineBulkActionConfirmModal(props: {
	pipelineName: string;
	action: string;
	setAction: (action: string) => void;
	selectedActivities: ActivityInfoWithStatus[];
	delayRefs: any;
}) {
	const [confirmed, setConfirmed] = useState(false);
	const [deleteConfirmed, setDeleteConfirmed] = useState(false);
	const [activityStatuses, setActivityStatuses] = useState<Record<number, OperationStatus>>({}); // live progress update

	const groupName = useGroupName();
	const alias = useAlias();

	// validation function that splits activities into valid/invalid arrays based on status
	const validateActivitiesForAction = (
		action: string,
		activities: ActivityInfoWithStatus[]
	): { valid: ActivityInfoWithStatus[]; invalid: ActivityInfoWithStatus[] } => {
		if (action === ActivityActions.delete) {
			return { valid: activities, invalid: [] };
		}
		const validStatuses = actionStatusRules[action] || [];
		const valid = activities.filter((a) => validStatuses.includes(a.status));
		const invalid = activities.filter((a) => !validStatuses.includes(a.status));
		return { valid, invalid };
	};

	const { valid, invalid } = validateActivitiesForAction(props.action, props.selectedActivities);
	const hasValidActivities = valid?.length > 0;
	const hasInvalidActivities = invalid?.length > 0;

	const executeBulkAction = useCallback(async () => {
		try {
			setActivityStatuses({});
			store.dispatch(
				actions.page.addToFlashbar({
					id: `bulk-operation-progress-${props.pipelineName}`,
					message: {
						type: 'info',
						header: `Performing bulk operation ${props.action.toLowerCase()} on ${
							valid?.length
						} activities...`,
						dismissible: false,
					},
				})
			);

			let completedCount = 0;

			for (const { activityId } of valid) {
				try {
					setActivityStatuses((prev) => ({ ...prev, [activityId]: OperationStatus.IN_PROGRESS }));

					if (props.action === ActivityActions.delete) {
						await DeleteActivityInfo({ activityId, groupName });
						store.dispatch(actions.data.clearData({ datatype: 'activity', dataId: activityId.toString() }));
					} else {
						await PutActivityInfo({
							groupName,
							activityId,
							body: {
								operation: props.action.toLowerCase() as keyof typeof keys.Operation,
								updatedBy: alias,
							},
						});
					}

					setActivityStatuses((prev) => ({ ...prev, [activityId]: OperationStatus.SUCCESS }));

					store.dispatch(
						actions.page.addToFlashbar({
							id: `bulk-operation-progress-${props.pipelineName}`,
							message: {
								type: 'success',
								header: `Bulk operation ${props.action.toLowerCase()} completed`,
								content: `Successfully completed for activity ${activityId} - ${++completedCount}/${
									valid?.length
								} activities.`,
								dismissible: true,
							},
						})
					);
				} catch (error) {
					setActivityStatuses((prev) => ({ ...prev, [activityId]: OperationStatus.ERROR }));

					console.error(`Bulk operation ${props.action} failed for activity ${activityId}:`, error);
					store.dispatch(
						actions.page.addToFlashbar({
							id: `action-failure-${activityId}`,
							message: {
								type: 'error',
								header: `Bulk operation ${props.action.toLowerCase()} failed`,
								content: `Failed on activity ${activityId}: ${
									error instanceof Error ? error.message : 'Unknown error'
								}`,
								dismissible: true,
							},
						})
					);
				}
			}
			store.dispatch(actions.page.removeFromFlashbar(`bulk-operation-progress-${props.pipelineName}`));
		} finally {
			fetchPipelineActivitiesAndInstances(props.pipelineName, groupName, { delays: props.delayRefs }, true);
		}
	}, [valid, props, groupName, alias, props.delayRefs]);

	// since the progress modal have the ability to clear the action after user is done with it, this is necessary
	// to reset the states to make sure it's in sync
	useConditionalEffect(() => {
		setConfirmed(false);
		setActivityStatuses({});
	}, [!props.action]);

	const handleModalClose = () => {
		props.setAction('');
		setDeleteConfirmed(false);
		setConfirmed(false);
		setActivityStatuses({});
	};

	return (
		<>
			<Modal
				onDismiss={handleModalClose}
				visible={props.action && !confirmed}
				closeAriaLabel="Close action modal"
				size="large"
				footer={
					<Box float="right">
						<SpaceBetween size="xs" direction="horizontal">
							<Button variant="link" iconName="close" onClick={handleModalClose}>
								Cancel
							</Button>
							<Button
								variant="primary"
								iconName="check"
								data-testid="confirm-modal-button"
								disabled={
									!hasValidActivities || (props.action === ActivityActions.delete && !deleteConfirmed)
								}
								onClick={() => {
									// execute bulk edit API calls and display the progress
									if (props.action !== '') {
										setConfirmed(true);
										executeBulkAction();
									}
									setDeleteConfirmed(false);
								}}
							>
								Submit bulk {props.action.toLowerCase()}
							</Button>
						</SpaceBetween>
					</Box>
				}
				header={`Confirm bulk edit`}
			>
				<SpaceBetween size="m">
					{hasInvalidActivities &&
						(hasValidActivities ? (
							<BulkActionInvalidStatusAlert />
						) : (
							<BulkActionNoValidStatusAlert action={props.action} />
						))}
					{hasValidActivities && (
						<>
							<div>
								The following activities <b>WILL</b> be updated, please review your selection and
								continue:
							</div>
							<Table
								data-testid="valid-activities-table"
								items={valid}
								columnDefinitions={activityTableColumns}
							/>
						</>
					)}

					{hasInvalidActivities && (
						<>
							<div>
								The following activities <b>WILL NOT</b> be updated due to incompatible statuses:
							</div>
							<Table
								data-testid="invalid-activities-table"
								items={invalid}
								columnDefinitions={activityTableColumns}
							/>
						</>
					)}
					{props.action === ActivityActions.delete && (
						<>
							<Alert type="warning" header="Permanent Action">
								Activity deletion is permanent and cannot be undone; are you sure you would like to
								proceed with the bulk delete of all {props.selectedActivities?.length} activities?
							</Alert>
							<Checkbox
								data-testid="delete-confirmation-checkbox"
								checked={deleteConfirmed}
								onChange={({ detail }) => setDeleteConfirmed(detail.checked)}
							>
								I confirm that I want to delete these activities
							</Checkbox>
						</>
					)}
				</SpaceBetween>
			</Modal>
			<PipelineBulkActionProgressModal
				pipelineName={props.pipelineName}
				action={confirmed ? props.action : ''} // only pass in the bulk edit action once it's confirmed
				setAction={props.setAction}
				validActivities={valid}
				activityStatuses={activityStatuses}
			/>
		</>
	);
}
